import React, { useState } from 'react';
import { Question, SurveyTemplate } from '../types/Survey';
import RankingQuestion from './QuestionTypes/RankingQuestion';
import MatrixQuestion from './QuestionTypes/MatrixQuestion';
import './SurveyPreviewModal.css';

interface SurveyPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  survey: {
    title: string;
    description: string;
    questions: Question[];
  } | SurveyTemplate;
  isTemplate?: boolean;
}

const SurveyPreviewModal: React.FC<SurveyPreviewModalProps> = ({
  isOpen,
  onClose,
  survey,
  isTemplate = false
}) => {
  const [answers, setAnswers] = useState<{ [key: string]: any }>({});
  const [currentPage, setCurrentPage] = useState(0);
  
  // 조건부 로직 평가 함수
  const evaluateConditionalLogic = (question: any) => {
    if (!question.conditionalLogic || question.conditionalLogic.length === 0) {
      return { show: true, skip: false };
    }

    // Check if any show_question logic exists
    const hasShowLogic = question.conditionalLogic.some(
      (logic: any) => logic.action === 'show' || logic.action === 'show_question'
    );

    // Default behavior: if has show logic, hide by default
    let defaultShow = !hasShowLogic;
    let result = { show: defaultShow, skip: false };

    for (const logic of question.conditionalLogic) {
      const sourceAnswer = answers[logic.sourceQuestionId];
      let conditionMet = false;
      
      // Parse logic.value if it's a JSON string
      let logicValue = logic.value;
      if (typeof logicValue === 'string' && (logicValue.startsWith('"') || logicValue.startsWith('['))) {
        try {
          logicValue = JSON.parse(logicValue);
        } catch (e) {
          // Keep original value if parsing fails
        }
      }

      switch (logic.condition) {
        case 'equals':
          conditionMet = sourceAnswer === logicValue;
          break;
        case 'not_equals':
          conditionMet = sourceAnswer !== logicValue;
          break;
        case 'contains':
          conditionMet = Array.isArray(sourceAnswer) 
            ? sourceAnswer.includes(logicValue)
            : String(sourceAnswer || '').includes(String(logicValue));
          break;
        case 'not_contains':
          conditionMet = Array.isArray(sourceAnswer) 
            ? !sourceAnswer.includes(logicValue)
            : !String(sourceAnswer || '').includes(String(logicValue));
          break;
        case 'greater_than':
          conditionMet = Number(sourceAnswer) > Number(logicValue);
          break;
        case 'less_than':
          conditionMet = Number(sourceAnswer) < Number(logicValue);
          break;
        case 'is_empty':
        case 'is_not_answered':
          conditionMet = sourceAnswer === undefined || sourceAnswer === '' || sourceAnswer === null;
          break;
        case 'is_not_empty':
        case 'is_answered':
          conditionMet = sourceAnswer !== undefined && sourceAnswer !== '' && sourceAnswer !== null;
          break;
      }

      if (conditionMet) {
        switch (logic.action) {
          case 'show':
          case 'show_question':
            return { show: true, skip: false };
          case 'hide':
          case 'hide_question':
            return { show: false, skip: false };
          case 'skip_to':
            return { show: true, skip: true, skipTo: logic.targetQuestionId };
          case 'end_survey':
            return { show: false, skip: false, endSurvey: true };
        }
      }
    }

    return result;
  };

  // 조건부 로직을 고려한 표시할 질문들 필터링
  const getVisibleQuestions = () => {
    return survey.questions.filter(question => {
      const logicResult = evaluateConditionalLogic(question);
      return logicResult.show;
    });
  };

  const visibleQuestions = getVisibleQuestions();
  const questionsPerPage = 5;
  const totalPages = Math.ceil(visibleQuestions.length / questionsPerPage);
  const currentQuestions = visibleQuestions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleCheckboxChange = (questionId: string, option: string, checked: boolean) => {
    const currentAnswers = answers[questionId] || [];
    if (checked) {
      setAnswers(prev => ({
        ...prev,
        [questionId]: [...currentAnswers, option]
      }));
    } else {
      setAnswers(prev => ({
        ...prev,
        [questionId]: currentAnswers.filter((a: string) => a !== option)
      }));
    }
  };

  const handleNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
  };

  const handlePrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  };

  const resetPreview = () => {
    setAnswers({});
    setCurrentPage(0);
  };

  if (!isOpen) return null;

  return (
    <div className="survey-preview-modal">
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-content">
        <div className="modal-header">
          <div>
            <h2>{'title' in survey ? survey.title : survey.name}</h2>
            <span className="preview-badge">
              {isTemplate ? '템플릿 미리보기' : '설문 미리보기'}
            </span>
          </div>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="survey-preview-container">
            <div className="survey-header">
              <h1>{'title' in survey ? survey.title : survey.name}</h1>
              {survey.description && <p>{survey.description}</p>}
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }}
                />
              </div>
              <span className="progress-text">
                {visibleQuestions.length > 0 ? `${currentPage + 1} / ${totalPages} 페이지` : '표시할 질문이 없습니다'}
              </span>
            </div>

            <div className="questions-container">
              {currentQuestions.map((question, index) => (
                <div key={question.id} className="preview-question">
                  <label className="question-label">
                    {currentPage * questionsPerPage + index + 1}. {question.question}
                    {question.required && <span className="required">*</span>}
                  </label>

                  {question.type === 'multiple-choice' && (
                    <div className="options-group">
                      {question.options?.map((option: string) => (
                        <label key={option} className="radio-option">
                          <input
                            type="radio"
                            name={question.id}
                            value={option}
                            checked={answers[question.id] === option}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {question.type === 'checkbox' && (
                    <div className="options-group">
                      {question.options?.map((option: string) => (
                        <label key={option} className="checkbox-option">
                          <input
                            type="checkbox"
                            value={option}
                            checked={(answers[question.id] || []).includes(option)}
                            onChange={(e) => handleCheckboxChange(question.id, option, e.target.checked)}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {question.type === 'text' && (
                    <textarea
                      className="text-answer"
                      value={answers[question.id] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      placeholder="답변을 입력하세요"
                      rows={4}
                    />
                  )}

                  {question.type === 'rating' && (
                    <div className="rating-group">
                      {Array.from(
                        { length: (question.maxRating || 5) - (question.minRating || 1) + 1 },
                        (_, i) => (question.minRating || 1) + i
                      ).map(rating => (
                        <label key={rating} className="rating-option">
                          <input
                            type="radio"
                            name={question.id}
                            value={rating}
                            checked={answers[question.id] === rating}
                            onChange={() => handleAnswerChange(question.id, rating)}
                          />
                          <span className="rating-label">{rating}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {question.type === 'dropdown' && (
                    <select
                      className="dropdown-answer"
                      value={answers[question.id] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    >
                      <option value="">선택하세요</option>
                      {question.options?.map((option: string) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}

                  {question.type === 'matrix' && (
                    <MatrixQuestion
                      question={question}
                      value={answers[question.id] || {}}
                      onChange={(value) => handleAnswerChange(question.id, value)}
                    />
                  )}

                  {question.type === 'ranking' && (
                    <RankingQuestion
                      question={question}
                      value={answers[question.id] || []}
                      onChange={(value) => handleAnswerChange(question.id, value)}
                    />
                  )}

                  {question.type === 'file-upload' && (
                    <div className="file-upload-preview">
                      <div className="file-upload-placeholder">
                        📎 파일 업로드 (미리보기에서는 비활성화됨)
                      </div>
                      {question.allowedFileTypes && question.allowedFileTypes.length > 0 && (
                        <div className="file-upload-info">
                          <small>허용 형식: {question.allowedFileTypes.join(', ')}</small>
                        </div>
                      )}
                      {question.maxFileSize && (
                        <div className="file-upload-info">
                          <small>최대 크기: {question.maxFileSize}MB</small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="navigation-buttons">
              {currentPage > 0 && (
                <button className="btn btn-secondary" onClick={handlePrevious}>
                  이전
                </button>
              )}
              <div className="nav-center">
                <button className="btn btn-outline" onClick={resetPreview}>
                  초기화
                </button>
              </div>
              {currentPage < totalPages - 1 ? (
                <button className="btn btn-primary" onClick={handleNext}>
                  다음
                </button>
              ) : (
                <button className="btn btn-success" onClick={() => {
                  resetPreview();
                  onClose();
                }}>
                  완료 (미리보기)
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyPreviewModal;